<script setup>
/**
 * Where the ledger is, as a square (stores/ledger-states.js): this device or
 * the cloud, readable or encrypted. The state it is in is marked; any other
 * can be chosen, and the move to it is shown before it is made — one step
 * along a side, two across (the second offered again after the reload the
 * first ends with: `next`).
 *
 * The forms: a passphrase (twice) to encrypt, and the recovery key shown once
 * after; a sign-in, kept local, for a move into a new encrypted org; a plain
 * confirmation for the rest. A move to or from the cloud is an owner's, and a
 * ledger comes off the server only when nobody else shares it.
 */
import { computed, onMounted, reactive, ref } from 'vue'
import Icon from '../ui/Icon.vue'
import { state, toast } from '../../stores/state'
import { count } from '../../stores/usage'
import { errorText, t } from '../../stores/i18n'
import * as cloud from '../../stores/cloud'
import * as moves from '../../stores/ledger-states'
import { markSignedIn, mightBeSignedIn } from '../../stores/sync'
import { unlockedRing } from '../../stores/sealed'
import { currentLedger } from '../../stores/ledgers'
import { downloadBackup } from '../../stores/backup'

const props = defineProps({ next: { type: String, default: null } })
const emit = defineEmits(['goto'])

const backedUp = ref(false)
const backingUp = ref(false)
async function backup() {
  backingUp.value = true
  try {
    await downloadBackup()
    backedUp.value = true
  } catch (err) {
    error.value = errorText(err)
  } finally {
    backingUp.value = false
  }
}

const STATES = [
  { id: 'local', where: 'device', kind: 'readable', icon: 'phone' },
  { id: 'sealed', where: 'device', kind: 'encrypted', icon: 'lock' },
  { id: 'cloud', where: 'cloud', kind: 'readable', icon: 'upload' },
  { id: 'vault', where: 'cloud', kind: 'encrypted', icon: 'lock' },
]
const here = moves.currentState()
const target = ref(null)
const busy = ref(false)
const error = ref('')
const recovery = ref('')
const form = reactive({ passphrase: '', again: '', name: '', email: '' })
const account = ref(state.cloud.account)
const people = ref(null)
const MIN = 8

const org = computed(() => state.cloud.org)
const owner = computed(() => org.value?.role === 'owner' || state.sync.role === 'owner')
const plan = computed(() => (target.value ? moves.route(here, target.value) : null))
const step = computed(() => plan.value?.steps[0] ?? null)

const inCloud = here === 'cloud' || here === 'vault'
/** Who shares it, asked again whenever a move is chosen: people come and go. */
const loadPeople = async () => {
  if (!inCloud) return
  people.value = undefined // being asked: no move off the server until it is known
  people.value = await cloud.members(org.value?.id).catch(() => null)
}

// How far a move's files are (each move carries them: stores/ledger-states.js).
const progress = ref(null)
const onProgress = (p) => (progress.value = p)

onMounted(async () => {
  if (props.next && moves.route(here, props.next)) choose(props.next)
  if (inCloud) loadPeople()
  else if (mightBeSignedIn()) account.value = await cloud.me().catch(() => null)
})

function choose(id) {
  target.value = target.value === id ? null : id
  error.value = ''
  form.passphrase = form.again = ''
  form.name = here === 'sealed' ? (currentLedger()?.name ?? '') : ''
  if (target.value) loadPeople()
}

/** Why the first step cannot be taken from here, or null. */
const blocked = computed(() => {
  const s = step.value
  if (!s) return null
  if (['down', 'encrypt', 'decrypt', 'sealedDown'].includes(s) && !owner.value) return t('states.ownerOnly')
  if (['down', 'sealedDown'].includes(s) && people.value && people.value.length > 1) return t('states.shared')
  return null
})
/** What the first step needs typed. */
// Encrypted, a ledger keeps its keyring wherever it goes: moving it between here
// and the cloud asks for nothing — but one encrypted before keyrings gets a new one.
const needsPassphrase = computed(() => ['seal', 'encrypt'].includes(step.value) || (step.value === 'sealedUp' && !unlockedRing()))
const needsSignIn = computed(() => step.value === 'sealedUp' && !account.value)
const ready = computed(
  () =>
    !blocked.value &&
    !needsSignIn.value &&
    !(['down', 'sealedDown'].includes(step.value) && people.value === undefined) &&
    (!needsPassphrase.value || (form.passphrase.length >= MIN && form.passphrase === form.again)),
)

const reload = () => location.reload()

async function go() {
  busy.value = true
  error.value = ''
  const passphrase = form.passphrase
  try {
    // The second step waits on the other side of the reload.
    if (plan.value.through) moves.setNextStep({ through: plan.value.through, to: target.value })
    let shown = null
    switch (step.value) {
      case 'up':
        moves.up()
        break
      case 'down':
        await moves.down({ org: org.value, onProgress })
        break
      case 'seal':
        shown = await moves.seal({ passphrase, onProgress })
        break
      case 'unseal':
        await moves.unseal({ onProgress })
        break
      case 'sealedUp':
        shown = await moves.sealedUp({ name: form.name.trim() || t('sync.defaultName'), passphrase, onProgress })
        break
      case 'sealedDown':
        shown = await moves.sealedDown({ org: org.value, onProgress })
        break
      case 'encrypt':
        shown = await moves.encrypt({ org: org.value, passphrase, onProgress })
        break
      case 'decrypt':
        await moves.decrypt({ org: org.value, onProgress })
        break
    }
    count('move', step.value)
    form.passphrase = form.again = ''
    // A recovery key is written down before the page reloads onto the new state.
    if (shown) recovery.value = shown
    else location.reload()
  } catch (err) {
    moves.setNextStep(null)
    error.value = err?.code === 'move-locked' ? t('cloud.moveLocked') : errorText(err)
  } finally {
    busy.value = false
  }
}

// Signing in without leaving this device: the link comes back here (main.js).
async function sendLink() {
  error.value = ''
  // The link reloads the page: this move is offered again when it is back.
  moves.setNextStep({ through: here, to: target.value })
  try {
    localStorage.setItem('okane.syncSignIn', '1')
  } catch {
    // The link then switches to the cloud instead.
  }
  try {
    await cloud.requestLink(form.email.trim(), state.settings.language)
    toast(t('sync.linkSent', { email: form.email.trim() }), 'good')
  } catch (err) {
    error.value = errorText(err)
  }
}
async function passkey() {
  try {
    await cloud.signInWithPasskey()
    markSignedIn(true)
    account.value = await cloud.me()
  } catch (err) {
    error.value = err?.name === 'NotAllowedError' ? t('cloud.passkeyCancelled') : errorText(err)
  }
}
</script>

<template>
  <section class="grid gap-3" data-states :data-here="here">
    <div>
      <h3 class="text-[13px] font-semibold">{{ t('states.title') }}</h3>
      <p class="text-[12.5px] text-muted">{{ t('states.lead') }}</p>
    </div>

    <!-- The square: where (rows) × how (columns). -->
    <div class="grid grid-cols-[auto_1fr_1fr] gap-2 text-[12.5px]">
      <span />
      <span class="px-1 text-faint">{{ t('states.readable') }}</span>
      <span class="px-1 text-faint">{{ t('states.encrypted') }}</span>
      <template v-for="row in ['device', 'cloud']" :key="row">
        <span class="self-center pr-1 text-faint">{{ t(`states.row.${row}`) }}</span>
        <button
          v-for="s in STATES.filter((x) => x.where === row)"
          :key="s.id"
          class="grid gap-1 rounded-xl border p-3 text-left transition-colors"
          :class="s.id === here ? 'border-accent bg-accent/10' : target === s.id ? 'border-text/40 bg-raised' : 'border-line-soft hover:bg-raised/60'"
          :disabled="s.id === here || !!recovery"
          :aria-pressed="target === s.id"
          :data-state="s.id"
          :data-current="s.id === here || undefined"
          @click="choose(s.id)"
        >
          <Icon :name="s.icon" :size="16" :class="s.id === here ? 'text-accent' : 'text-muted'" />
          <span class="font-semibold">{{ t(`states.${s.id}`) }}</span>
          <span class="text-[11.5px] text-faint">{{ s.id === here ? t('states.here') : t(`states.${s.id}Lead`) }}</span>
        </button>
      </template>
    </div>

    <!-- The key, once: the page reloads onto the new state after. -->
    <div v-if="recovery" class="grid gap-2 rounded-xl border border-warn/40 bg-warn/5 p-3" data-recovery>
      <p class="text-[13px] font-semibold">{{ t('sync.recoveryTitle') }}</p>
      <p class="text-[12.5px] text-muted">{{ t('sync.recoveryLead') }}</p>
      <code class="select-all break-all rounded-lg bg-raised px-3 py-2 font-mono text-[13px]" data-recovery-key>{{ recovery }}</code>
      <button class="btn-primary justify-self-start" data-recovery-done @click="reload">
        <Icon name="check" :size="15" /> {{ t('cloud.encryptContinue') }}
      </button>
    </div>

    <!-- The move to the chosen state. -->
    <form v-else-if="plan" class="grid gap-2 rounded-xl border border-line-soft p-3" :data-move="step" @submit.prevent="go">
      <p v-if="plan.through" class="text-[12.5px] text-muted" data-two-steps>
        {{ t('states.twoSteps', { first: t(`states.step.${plan.steps[0]}`), second: t(`states.step.${plan.steps[1]}`) }) }}
      </p>
      <p class="text-[13px]">{{ t(`states.do.${step}`) }}</p>
      <p v-if="blocked" class="text-[12.5px] text-warn" data-blocked>{{ blocked }}</p>

      <!-- Into a new encrypted org: signed in first, the device kept local. -->
      <template v-if="needsSignIn">
        <p class="text-[12.5px] text-muted">{{ t('sync.signInFirst') }}</p>
        <div class="flex gap-2" data-signin>
          <input v-model="form.email" class="input" type="email" autocomplete="email" :placeholder="t('signin.email')" :aria-label="t('signin.email')" />
          <button type="button" class="btn-soft shrink-0 whitespace-nowrap" :disabled="!form.email.includes('@')" @click="sendLink">{{ t('sync.sendLink') }}</button>
        </div>
        <button v-if="cloud.passkeysSupported()" type="button" class="btn-ghost justify-self-start" @click="passkey">
          <Icon name="key" :size="15" /> {{ t('signin.passkey') }}
        </button>
      </template>
      <template v-else-if="step === 'sealedUp'">
        <p class="text-[12.5px] text-faint">{{ t('sync.signedInAs', { email: account.user.email }) }}</p>
        <input v-model="form.name" class="input" :placeholder="t('sync.defaultName')" :aria-label="t('sync.name')" data-org-name />
      </template>

      <template v-if="needsPassphrase && !blocked">
        <input v-model="form.passphrase" class="input" type="password" autocomplete="new-password" :placeholder="t('sync.passphrase')" :aria-label="t('sync.passphrase')" data-passphrase />
        <input v-model="form.again" class="input" type="password" autocomplete="new-password" :placeholder="t('sync.passphraseAgain')" :aria-label="t('sync.passphraseAgain')" data-again />
        <p v-if="form.passphrase && form.passphrase.length < MIN" class="text-[12px] text-faint">{{ t('sync.passphraseShort', { n: MIN }) }}</p>
        <p v-else-if="form.again && form.again !== form.passphrase" class="text-[12px] text-warn">{{ t('sync.passphraseDiffers') }}</p>
      </template>

      <p v-if="busy && progress?.total" class="text-[12.5px] text-muted" data-move-files>{{ t('states.files', progress) }}</p>
      <div class="flex gap-2">
        <button class="btn-primary" :disabled="!ready || busy" data-move-go>
          {{ busy ? t('states.working') : t(`states.go.${step}`) }}
        </button>
        <button type="button" class="btn-ghost" @click="choose(target)">{{ t('common.cancel') }}</button>
      </div>
    </form>

    <!-- Before any move: every move copies, checks, and then deletes what it left. -->
    <div v-if="!recovery" class="grid gap-2 rounded-xl border border-line-soft bg-raised/50 p-3" data-backup>
      <p class="text-[12.5px] text-muted">{{ t('cloud.backupFirst') }}</p>
      <div class="flex flex-wrap items-center gap-2">
        <button type="button" class="btn-soft" :disabled="backingUp" @click="backup">
          <Icon name="download" :size="15" /> {{ t('cloud.backup') }}
        </button>
        <button type="button" class="btn-ghost !text-[12.5px]" @click="emit('goto', 'data')">{{ t('cloud.backupProtected') }}</button>
        <span v-if="backedUp" class="flex items-center gap-1 text-[12.5px]"><Icon name="check" :size="14" class="text-accent" />{{ t('cloud.backedUp') }}</span>
      </div>
    </div>

    <p v-if="error" class="text-[12.5px] text-warn" role="alert">{{ error }}</p>
  </section>
</template>
